import { Button, Paper } from '@mui/material';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys, QueryKeys } from '../../../api/config';
import { useAddStandardRouteMutation } from '../../../api/mutations/add/use-add-standard-route-mutation';
import { useEditStandardRouteMutation } from '../../../api/mutations/edit/use-edit-standard-route-mutation';
import { useOrganizationQuery } from '../../../api/queries/use-organization-query';
import { useStaticQuery } from '../../../api/queries/use-static-query';
import { type AvoidanceZone, type CreateStandardRoute, type Organization, type StandardRoute } from '../../../types';
import { AvoidanceZoneBox } from '../../here-maps/avoidance-zone-box';
import { HereMap, HereMapRefObject } from '../../here-maps/here-map';
import { OrganizationLocationBoxes } from '../../here-maps/organization-location-boxes';
import { StandardRouteLines } from '../../here-maps/standard-route-lines';
import { Content } from '../../shared/containers/content';
import { ModalDialog } from '../../shared/containers/modal-dialog';
import { AdminBreadCrumbHeader } from '../admin-bread-crumb-header';
import { EditCreateStandardRoute } from './edit-create-standard-route';
import { StandardRouteList } from './standard-route-list';

export const StandardRouteManagement: React.FC = () => {
    const [shownRoutes, setShownRoutes] = React.useState<number[]>([]);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [selectedStandardRoute, setSelectedStandardRoute] = React.useState<StandardRoute | null>(null);
    const { data: organization } = useOrganizationQuery<Organization>('');
    const { data: standardRoutes = [] } = useOrganizationQuery<StandardRoute[]>(OrgQueryKeys.standardRoutes);
    const { data: avoidanceZones = [] } = useStaticQuery<AvoidanceZone[]>(QueryKeys.avoidanceZones);
    const { mutate: addStandardRoute } = useAddStandardRouteMutation();
    const { mutate: editStandardRoute } = useEditStandardRouteMutation();
    const mapRef = React.useRef<HereMapRefObject>(null);

    const filteredStandardRoutes = React.useMemo(() => {
        return standardRoutes.filter(x => !x.deletedAt);
    }, [standardRoutes]);

    const renderShownRoutes = React.useMemo(() => {
        return (
            shownRoutes.map(srId => {
                const item = filteredStandardRoutes.find(x => x.id === srId);
                if (item) {
                    return (
                        <StandardRouteLines
                            key={srId}
                            flexiblePolyline={item.flexiblePolyline}
                            onRouteDrawn={() => mapRef.current?.fitMapToAllObjects(true, undefined, false)}
                        />
                    )
                }
            })
        )
    }, [shownRoutes]);

    const avoidanceZoneBoxes = avoidanceZones.filter(x => !x.deactivatedAt).map((avoidanceZone, i) =>
        <AvoidanceZoneBox key={i} avoidanceZone={avoidanceZone} />
    );

    const handleEditOpen = (s: StandardRoute) => {
        setSelectedStandardRoute(s);
        setDialogOpen(true);
    }

    const onSubmit = (standardRoute: StandardRoute | CreateStandardRoute) => {
        if ('id' in standardRoute) {
            editStandardRoute(standardRoute);
        } else {
            addStandardRoute(standardRoute);
        }

        setSelectedStandardRoute(null);
        setDialogOpen(false);
    };

    return (
        <>
            <AdminBreadCrumbHeader />
            <Content style={{ height: '100%', overflow: 'hidden' }}>
                <Paper
                    sx={{
                        display: 'flex',
                        height: '100%',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '1em' }}>
                        <Button
                            variant='contained'
                            onClick={() => setDialogOpen(true)}
                        >
                            {L.createNew()}
                        </Button>
                        <StandardRouteList
                            standardRoutes={filteredStandardRoutes}
                            shownRoutes={shownRoutes}
                            setShownRoutes={setShownRoutes}
                            handleEditOpen={handleEditOpen}
                        />
                    </div>
                    {organization &&
                        <HereMap
                            ref={mapRef}
                            centerPosition={{ lat: organization.latitude, lng: organization.longitude }}
                        >
                            {renderShownRoutes}
                            {avoidanceZoneBoxes}
                            <OrganizationLocationBoxes />
                        </HereMap>
                    }
                    <ModalDialog
                        open={dialogOpen}
                        onClose={() => {
                            setSelectedStandardRoute(null);
                            setDialogOpen(false);
                        }}
                        title={selectedStandardRoute ? L.editStandardRoute() : L.addStandardRoute()}
                    >
                        <EditCreateStandardRoute
                            existingStandardRoute={selectedStandardRoute}
                            onSubmit={onSubmit}
                            filteredStandardRoutes={filteredStandardRoutes}
                            avoidanceZoneBoxes={avoidanceZoneBoxes}
                        />
                    </ModalDialog>
                </Paper>
            </Content>
        </>
    );
};
